import React, { useEffect, useRef, useState } from "react";
import { Clock, Eraser, Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { adminApi, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Field, Area, Select, Btn, panel } from "@/admin/kit";

const STAMP = /^\s*\[\d{1,2}:\d{2}(?:\.\d+)?\]\s?/;
const fmt = (t) => `[${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}]`;

export const LyricsStamper = ({ lyrics, previewUrl, fullMediaId, onChange }) => {
  const audio = useRef();
  const [t, setT] = useState(0);
  const [fullSrc, setFullSrc] = useState(null);
  const [fullErr, setFullErr] = useState(null);
  useEffect(() => {
    if (!fullMediaId) { setFullSrc(null); return undefined; }
    let url;
    adminApi.get(`/admin/media/audio/${fullMediaId}/full`, { responseType: "blob", timeout: 120000 })
      .then((res) => { url = URL.createObjectURL(res.data); setFullSrc(url); setFullErr(null); })
      .catch(async (e) => {
        let msg = e.response?.data?.detail;
        if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ } }
        setFullErr(formatApiErrorDetail(msg) || "Full song unavailable — using the preview");
      });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fullMediaId]);
  const lines = (lyrics || "").split("\n");
  const nextIdx = lines.findIndex((l) => l.trim() && !STAMP.test(l));
  const stamp = () => {
    if (nextIdx < 0) return toast("Every line already has a time");
    const cur = audio.current?.currentTime || 0;
    const next = [...lines];
    next[nextIdx] = `${fmt(cur)} ${next[nextIdx].trim()}`;
    onChange(next.join("\n"));
  };
  const clear = () => onChange(lines.map((l) => l.replace(STAMP, "")).join("\n"));
  if (!previewUrl) return <p style={{ color: "var(--ash)", fontSize: ".72rem" }}>Upload audio to unlock the sing-along timer.</p>;
  const usingFull = Boolean(fullMediaId && fullSrc);
  return (
    <div className="p-3 grid gap-2" style={panel} data-testid="lyrics-stamper">
      <p className="font-caps text-gold" style={{ fontSize: ".55rem" }}>Sing-along timer · {usingFull ? "full song" : "90-second preview"}</p>
      <p style={{ color: "var(--ash)", fontSize: ".72rem" }}>Play {usingFull ? "the song" : "the preview"} and tap <b>Stamp this line</b> the moment each line starts. Next up: <span style={{ color: "var(--parchment)" }}>{nextIdx >= 0 ? `“${lines[nextIdx].trim().slice(0, 60)}”` : "all lines timed"}</span></p>
      {fullMediaId && !fullSrc && !fullErr && <p style={{ color: "var(--ash)", fontSize: ".72rem" }} data-testid="lyrics-stamper-loading">Loading the full song…</p>}
      {fullErr && <p style={{ color: "var(--ash)", fontSize: ".72rem" }} data-testid="lyrics-stamper-fallback">{fullErr}</p>}
      <audio ref={audio} src={usingFull ? fullSrc : mediaUrl(previewUrl)} controls preload="metadata" className="w-full" onTimeUpdate={(e) => setT(e.target.currentTime)} data-testid="lyrics-stamper-audio" />
      <div className="flex flex-wrap gap-2 items-center">
        <Btn type="button" onClick={stamp} disabled={nextIdx < 0} data-testid="lyrics-stamp"><Clock size={13} /> Stamp this line at {fmt(t)}</Btn>
        <Btn type="button" variant="ghost" onClick={clear} data-testid="lyrics-clear-times"><Eraser size={13} /> Clear all times</Btn>
      </div>
    </div>
  );
};

const STYLES = [["zoom", "Slow zoom"], ["slide", "Slide across"], ["pulse", "Pulse to the beat"]];

export const ReadyToPost = ({ release, id, isUpcoming, onCaptionSaved }) => {
  const [kit, setKit] = useState(null);
  const [caption, setCaption] = useState("");
  const [style, setStyle] = useState("zoom");
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { adminApi.get(`/admin/releases/${id}/post-kit`).then(({ data }) => { setKit(data); setCaption(data.caption); }).catch(() => {}); }, [id]);
  const download = async (path, name, key) => {
    setBusy(key);
    try {
      const res = await adminApi.get(path, { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ } }
      toast.error(formatApiErrorDetail(msg) || "Download failed");
    } finally { setBusy(null); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Caption copied"); }
    catch { window.prompt("Copy this caption", caption); }
  };
  const saveCaption = async () => {
    try { await adminApi.put(`/admin/releases/${id}`, { post_caption: caption }); onCaptionSaved?.(caption); toast.success("Caption saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!kit) return null;
  const slug = (release.title || "release").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="p-4 grid gap-3" style={{ border: "1px solid rgba(200,155,60,.3)", borderRadius: "var(--radius)" }} data-testid="ready-to-post">
      <div className="flex items-center justify-between gap-3">
        <p className="font-caps text-gold" style={{ fontSize: ".62rem" }}>Ready to post</p>
        <span className="font-caps text-ash" style={{ fontSize: ".5rem" }}>{isUpcoming ? "Pre-save push" : "Out-now push"}</span>
      </div>
      <Field label="Caption + hashtags" help="Pre-written for this release — edit freely, then copy it into Instagram, Facebook or TikTok.">
        <Area rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="post-caption" />
      </Field>
      <div className="flex flex-wrap gap-2 items-center">
        <Btn type="button" onClick={copy} data-testid="post-caption-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy caption"}</Btn>
        <Btn type="button" variant="ghost" onClick={saveCaption} data-testid="post-caption-save">Save caption</Btn>
        {caption !== kit.suggested && <Btn type="button" variant="ghost" onClick={() => setCaption(kit.suggested)} data-testid="post-caption-reset">Reset to suggested</Btn>}
      </div>
      {kit.has_artwork && (
        <div className="flex flex-wrap gap-2 items-center pt-2" style={{ borderTop: "var(--rule)" }}>
          <Btn type="button" variant="ghost" disabled={busy === "card"} data-testid="download-story-card" onClick={() => download(`/admin/releases/${id}/story.jpg`, `${slug}-story.jpg`, "card")}>
            <Download size={13} /> {busy === "card" ? "Building…" : "Story card (9:16 image)"}
          </Btn>
          <Select value={style} onChange={(e) => setStyle(e.target.value)} style={{ maxWidth: 190 }} data-testid="clip-style">
            {STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Btn type="button" variant="ghost" disabled={busy === "clip"} data-testid="download-story-clip" onClick={() => download(`/admin/releases/${id}/story.mp4?style=${style}`, `${slug}-story-${style}.mp4`, "clip")}>
            <Download size={13} /> {busy === "clip" ? "Rendering clip (about 20s)…" : "15-second Reels clip"}
          </Btn>
          <span style={{ color: "var(--ash)", fontSize: ".72rem" }}>{kit.has_audio ? "Clip uses the first 15 seconds of your preview audio." : "Upload audio to add sound to the clip."}</span>
        </div>
      )}
      <p style={{ color: "var(--ash)", fontSize: ".72rem" }}>Share link for the caption's “link in bio”: <span style={{ color: "var(--parchment)" }} data-testid="post-share-url">{kit.share_url}</span></p>
    </div>
  );
};
